import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { cardHolds, cardPackId, type PackVariant } from '@/lib/designPackAccess';
import { lookName } from '@/lib/studioB/takeover';
import type { SavedLook } from '@/services/api';

export interface LookCard {
  id: string;
  name: string;
  whenToUse: string;
  previewImages: string[];
  packId?: string;
  variants?: PackVariant[];
  shelf?: { place: 'first' | 'more'; bestFor: string; clipTheme?: string };
}

const CLIPS = '/design-packs/looks';
const IVY_SAMPLES = ['nutshell-world', 'drawn-explainer', 'toy-world'];

/** A muted loop that plays only while on screen. With reduced motion it stays on its poster. */
function LookClip({ id }: { id: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const seen = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) { video.pause(); return; }
      if (!video.src) video.src = video.dataset.src ?? '';
      void video.play().catch(() => {});
    }, { threshold: 0.25 });
    seen.observe(video);
    return () => seen.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      muted
      loop
      playsInline
      preload="none"
      poster={`${CLIPS}/${id}.jpg`}
      data-src={`${CLIPS}/${id}.mp4`}
      className="w-full aspect-video rounded-lg object-cover bg-muted"
    />
  );
}

interface TileProps {
  picked: boolean;
  name: string;
  line: string;
  /** A client's own card: its line is the pack's notes for Ivy, too long and too internal to show whole. */
  clampLine?: boolean;
  onPick: () => void;
  picture: React.ReactNode;
}

function Tile({ picked, name, line, clampLine, onPick, picture }: TileProps) {
  return (
    <button
      type="button"
      aria-pressed={picked}
      onClick={onPick}
      className={`relative w-full text-left p-2 rounded-xl border-2 transition-colors ${
        picked ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-transparent hover:bg-muted/40'
      }`}
    >
      {picked && (
        <div className="absolute top-3.5 right-3.5 z-10 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
          <Check className="h-3 w-3 text-primary-foreground" />
        </div>
      )}
      {picture}
      <h4 className="font-semibold text-sm mt-2 mb-0.5">{name}</h4>
      <p className={`text-xs text-muted-foreground leading-relaxed${clampLine ? ' line-clamp-2' : ''}`}>{line}</p>
    </button>
  );
}

function LookTile({ card, value, onPick }: { card: LookCard; value: string | null; onPick: (id: string) => void }) {
  const picked = cardHolds(card, value);
  const own = cardPackId(card);
  return (
    <div className="flex flex-col gap-1.5">
      <Tile picked={picked} name={card.name} line={card.shelf?.bestFor ?? card.whenToUse} onPick={() => onPick(own)} picture={<LookClip id={card.id} />} />
      {picked && !!card.variants?.length && (
        <div className="flex flex-wrap gap-1 px-2">
          {[{ packId: own, name: 'As shown' }, ...card.variants.filter((v) => v.packId !== own)].map((variant) => (
            <button
              key={variant.packId}
              type="button"
              onClick={() => onPick(variant.packId)}
              className={`px-2 py-0.5 rounded-full border text-[11px] transition-colors ${
                value === variant.packId ? 'border-primary bg-primary/10 text-primary' : 'border-border/60 text-muted-foreground hover:border-border'
              }`}
            >
              {variant.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const byName = (a: LookCard, b: LookCard) => a.name.localeCompare(b.name, undefined, { numeric: true });

/** A saved look's picture: its style's still only when that's the theme the still shows, or it would mislead; otherwise its own colours. */
function SavedPicture({ look, base }: { look: SavedLook; base?: LookCard }) {
  if (base && look.theme && base.shelf?.clipTheme === look.theme) {
    return <img src={`${CLIPS}/${base.id}.jpg`} alt="" loading="lazy" className="w-full aspect-video rounded-lg object-cover bg-muted" />;
  }
  return (
    <div data-look-colours className="w-full aspect-video rounded-lg overflow-hidden flex bg-muted" aria-hidden>
      {look.colors.map((colour, i) => <span key={`${colour}-${i}`} className="flex-1" style={{ background: colour }} />)}
    </div>
  );
}

function savedLine(look: SavedLook, base?: LookCard): string {
  const whose = look.scope === 'team' ? 'Your team’s look' : 'Your saved look';
  if (!base) return whose;
  const from = look.theme ? `${base.name} · ${look.theme[0].toUpperCase()}${look.theme.slice(1)}` : base.name;
  return look.scope === 'team' ? `${whose}, based on ${from}` : `Based on ${from}`;
}

interface LookGalleryProps {
  videoCards: LookCard[];
  lessonCards: LookCard[];
  savedLooks?: SavedLook[];
  value: string | null;
  onChange: (packId: string | null) => void;
}

/** The looks a person can pick: Ivy's pick first, then the first shelf, then the rest. A card without a shelf is a client's own look. */
export function LookGallery({ videoCards, lessonCards, savedLooks = [], value, onChange }: LookGalleryProps) {
  const [lessonsOnly, setLessonsOnly] = useState(false);
  const everyLook = [...videoCards, ...lessonCards.filter((l) => !videoCards.some((v) => v.id === l.id))];
  const cards = lessonsOnly ? lessonCards : everyLook;
  const onShelf = (place: 'first' | 'more') => cards.filter((c) => c.shelf?.place === place).sort(byName);
  const own = cards.filter((c) => !c.shelf);
  const saved = savedLooks.filter((l) => !lessonsOnly || l.modes.includes('edu'));
  const grid = 'grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3 gap-2';
  const small = 'grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2';
  const chip = (on: boolean) => `px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${on ? 'bg-foreground text-background border-foreground' : 'border-border/60 text-foreground hover:bg-muted/40'}`;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter looks">
        <button type="button" aria-pressed={!lessonsOnly} className={chip(!lessonsOnly)} onClick={() => setLessonsOnly(false)}>All looks</button>
        <button type="button" aria-pressed={lessonsOnly} className={chip(lessonsOnly)} onClick={() => setLessonsOnly(true)}>Works in lessons</button>
      </div>
      {own.length > 0 && (
        <div className={grid}>
          {own.map((card) => (
            <Tile
              key={card.id}
              picked={cardHolds(card, value)}
              name={card.name}
              line={card.whenToUse}
              clampLine
              onPick={() => onChange(cardPackId(card))}
              picture={<img src={card.previewImages[0]} alt="" className="w-full aspect-video rounded-lg object-cover bg-muted" />}
            />
          ))}
        </div>
      )}
      {saved.length > 0 && (
        <>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Your looks</h3>
          <div className="flex items-start gap-2 overflow-x-auto pb-1" data-shelf="yours">
            {saved.map((look) => {
              const base = everyLook.find((c) => c.id === look.style);
              return (
                <div key={look.ref} className="w-44 sm:w-56 shrink-0">
                  <Tile picked={value === look.ref} name={lookName(look.ref)} line={savedLine(look, base)} onPick={() => onChange(look.ref)} picture={<SavedPicture look={look} base={base} />} />
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className={grid} data-shelf="first">
        <Tile
          picked={!value}
          name="Ivy picks"
          line="The default. You see three looks at the first stop and can switch for free."
          onPick={() => onChange(null)}
          picture={
            <div className="w-full aspect-video rounded-lg overflow-hidden grid grid-cols-3 gap-0.5 bg-primary/10" aria-hidden>
              {IVY_SAMPLES.map((id) => <img key={id} src={`${CLIPS}/${id}.jpg`} alt="" loading="lazy" className="w-full h-full object-cover" />)}
            </div>
          }
        />
        {onShelf('first').map((card) => <LookTile key={card.id} card={card} value={value} onPick={onChange} />)}
      </div>
      {onShelf('more').length > 0 && (
        <>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">More looks</h3>
          <div className={small} data-shelf="more">
            {onShelf('more').map((card) => <LookTile key={card.id} card={card} value={value} onPick={onChange} />)}
          </div>
        </>
      )}
    </div>
  );
}
